import React, { ComponentType } from "react"
import { NodeProps, useReactFlow } from "reactflow"
import { Grid, Tooltip } from "@material-ui/core"
import { themeColors } from "letalkui"

import { BlockNodeData } from "@/protocols/chatBotFlow"
import { AgentOption } from "@/protocols/chatBotConstructor"

import usePageFocus from "@/hooks/usePageFocus"
import useChatBotFlowBlockDetails from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowBlockDetails"

import EditTimeoutConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/EditTimeoutConditionSection"
import BaseBlock from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BaseBlock"
import BlockBodySection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySection"
import BlockBodySectionContainer from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionContainer"
import BlockBodySectionGroup from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionGroup"
import ContrastChip from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ContrastChip"
import BlockBodySectionHandle from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionHandle"
import AfterActionConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/AfterActionConditionSection"
import UnsupportedMessageTypeConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIAgentBlock/UnsupportedMessageTypeConditionSection"

import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"

import AIAgentSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIAgentBlock/AIAgentSectionEditor"
import EditMaxBlockCallingConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIAgentBlock/EditMaxBlockCallingConditionSection"
import PluginStatusReport from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/PluginStatusReport"

import { ValidationContent } from "@/hooks/useActiveCampaignApi"

import { getContinueConditionRuleFormattedForCreation } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockRule"
import { generateUUID } from "@/utils/id"
import { getCurrentAgent } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"

import useCustomStyles from "@/styles/custom"

const AIAgentBlock: ComponentType<NodeProps<BlockNodeData>> = (node) => {
	const reactFlow = useReactFlow()

	const chatBotFlowBlockDetails = useChatBotFlowBlockDetails({ chatBotFlowBlockId: node.data.chatBotFlowBlockId })
	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()

	const nextChatBotFlowBlockRules = chatBotFlowBlockDetails.nextChatBotFlowBlockRules
	const continueConditionRules = nextChatBotFlowBlockRules.filter(({ category }) => category === "continue-condition")

	const currentAgent = getCurrentAgent(chatBotFlowConstructorStore.constructionResources, chatBotFlowBlockDetails.content)
	const chatBotFlowBlockRuleFormattedForCreation = getContinueConditionRuleFormattedForCreation(chatBotFlowBlockDetails.blockConstructionResource?.available_next_block_flow_rules)

	const customClasses = useCustomStyles()

	const handleDeleteAllOutputConditionsInBlock = () => {
		continueConditionRules.forEach(rule => {
			const edges = reactFlow.getEdges()
			const edge = edges.find(edge => edge.sourceHandle === rule.id)

			if (edge) {
				reactFlow.deleteElements({ edges: [edge] })
			}

			chatBotFlowBlockDetails.changeRule("DELETE", rule)
		})
	}

	const handleCreateOutputConditionsInBlock = (agentOutputs?: AgentOption["associatedOutputs"]) => {
		handleDeleteAllOutputConditionsInBlock()

		agentOutputs?.forEach(output => {
			chatBotFlowBlockDetails.changeRule("CREATE", {
				id: generateUUID(),
				next_chat_bot_flow_block_id: null,
				validations: [{
					...chatBotFlowBlockRuleFormattedForCreation.validations[0],
					second_param: output.slug
				}],
				logic: "and",
				slug: "after-action",
				category: "continue-condition"
			})
		})
	}

	const handleSaveAgent = (agent: AgentOption) => {
		chatBotFlowBlockDetails.changeContent({
			agent: {
				id: agent.id
			}
		})

		handleCreateOutputConditionsInBlock(agent?.associatedOutputs)
	}

	const isPreview = chatBotFlowConstructorStore.isPreview

	usePageFocus({
		onFocus: async () => {
			if (!isPreview) {
				await chatBotFlowConstructorStore.loadSpecificConstructionResources("agents")
			}
		}
	})

	const hasCurrentAgent = Boolean(currentAgent)
	const savedOutputSlugsKey = continueConditionRules
		.map(rule => rule.validations[0]?.second_param)
		.filter((slug): slug is string => Boolean(slug))
		.sort()
		.join(",")
	const currentOutputSlugsKey = currentAgent?.associatedOutputs?.map(output => output.slug).sort().join(",") ?? ""
	const hasOutdatedOutputs = hasCurrentAgent && savedOutputSlugsKey !== currentOutputSlugsKey

	const handleUpdateOutputConditions = async () => {
		const currentOutputs = currentAgent?.associatedOutputs ?? []
		const currentSlugs = currentOutputs.map(output => output.slug)

		continueConditionRules.forEach(rule => {
			const slug = rule.validations[0]?.second_param
			const isOutputStillPresent = Boolean(slug) && currentSlugs.includes(slug as string)

			if (!isOutputStillPresent) {
				chatBotFlowBlockDetails.changeRule("DELETE", rule)
			}
		})

		const remainingSlugs = continueConditionRules
			.map(rule => rule.validations[0]?.second_param)
			.filter((slug): slug is string => Boolean(slug))

		currentOutputs
			.filter(output => !remainingSlugs.includes(output.slug))
			.forEach(output => {
				chatBotFlowBlockDetails.changeRule("CREATE", {
					id: generateUUID(),
					next_chat_bot_flow_block_id: null,
					validations: [{
						...chatBotFlowBlockRuleFormattedForCreation.validations[0],
						second_param: output.slug
					}],
					logic: "and",
					slug: "after-action",
					category: "continue-condition"
				})
			})
	}

	const outdatedOutputsAlertContent: ValidationContent = {
		type: "warning",
		text: <>As condições de saída deste bloco estão desatualizadas em relação ao agente selecionado. Clique no botão para atualizar.</>
	}

	return (
		<BaseBlock
			node={node}
		>
			<BlockBodySection
				fullWidth
			>
				{!isPreview && hasOutdatedOutputs && (
					<PluginStatusReport
						pluginStatusContent={outdatedOutputsAlertContent}
						onUpdate={handleUpdateOutputConditions}
						autoLoading
						refreshTooltip="Atualizar condições de saída"
					/>
				)}

				<AIAgentSectionEditor
					onSave={handleSaveAgent}
					chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
					selectedAgent={currentAgent}
				>
					<Grid
						container
						direction="column"
					>
						<BlockBodySectionContainer
							text={currentAgent ? (
								<Tooltip
									title={currentAgent.name}
									placement="right-end"
								>
									<Grid
										style={{
											width: "100%"
										}}
									>
										<ContrastChip
											title={currentAgent.name}
											backgroundColor={themeColors.colors.system.blue[400]}
											className={customClasses.inlineText}
											style={{
												display: "inline-flex"
											}}
										/>
									</Grid>
								</Tooltip>
							) : ""}
							title="Agente:"
							placeholder="Nenhum agente selecionado"
						/>
					</Grid>
				</AIAgentSectionEditor>
			</BlockBodySection>

			<BlockBodySectionGroup>
				{continueConditionRules?.map(rule => {
					const outputName = currentAgent?.associatedOutputs.find(output => output.slug === rule.validations[0].second_param)?.name

					if (!outputName) {
						return null
					}

					return (
						<BlockBodySection
							key={rule.id}
						>
							<BlockBodySectionHandle
								chatBotFlowBlockRule={rule}
							>
								<BlockBodySectionContainer
									styles={{ width: 220, minHeight: 40, padding: "8px 16px" }}
									text={outputName}
								/>
							</BlockBodySectionHandle>
						</BlockBodySection>
					)
				})}
			</BlockBodySectionGroup>

			<EditMaxBlockCallingConditionSection
				nextChatBotFlowBlockRules={nextChatBotFlowBlockRules}
				onSave={(chatBotFlowBlockRule) => chatBotFlowBlockDetails.changeRule("UPDATE", chatBotFlowBlockRule)}
				renderText={(value) => <span>após {value}</span>}
			/>

			<EditTimeoutConditionSection
				nextChatBotFlowBlockRules={nextChatBotFlowBlockRules}
				onSave={(chatBotFlowBlockRule) => chatBotFlowBlockDetails.changeRule("UPDATE", chatBotFlowBlockRule)}
				renderText={(timeText) => <span>{timeText} sem interação</span>}
				chatBotFlowChannelType={node.data.chatBotFlowChannelType}
			/>

			<AfterActionConditionSection
				nextChatBotFlowBlockRules={nextChatBotFlowBlockRules}
				text="Se o agente estiver desativado"
			/>

			<UnsupportedMessageTypeConditionSection
				nextChatBotFlowBlockRules={nextChatBotFlowBlockRules}
				text="Se o agente não conseguir interpretar a mensagem"
			/>
		</BaseBlock>
	)
}

export default AIAgentBlock
